import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getStoredPageSize, setStoredPageSize } from '@/components/Pagination';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { toast } from 'sonner';
import {
  Plus, Edit2, Trash2, Search, ChevronDown, MoreVertical,
  FileText, Shield, Calendar, Users, AlertCircle,
  CheckCircle, Clock, Building, BookOpen, X,
  BarChart3, ArrowUpDown, ArrowUp, ArrowDown, Eye
} from 'lucide-react';
import { TOOL_REGISTRY as toolConfigs } from '@/config/tools';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { PageTransition } from '@/components/PageTransition';
import { AddQualification } from '@/components/modals/qualifications/AddQualification';
import { EditQualification } from '@/components/modals/qualifications/EditQualification';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Table, TableCard } from '@/components/application/table/table-fixed';
import { useComplianceStats } from '@/hooks/useComplianceStats';

// Generate skeleton placeholder items for loading state
const skeletonItems = Array.from({ length: 8 }, (_, i) => ({
  id: `skeleton-${i}`,
  name: '',
  priority: '',
  calculated_required: null,
  renewal_period: '',
  isLoading: true,
}));

// Helper to get priority badge
const getPriorityBadge = (priority) => {
  switch (priority) {
    case 'high':
      return <Badge variant="destructive">High</Badge>;
    case 'medium':
      return <Badge variant="warning">Medium</Badge>;
    case 'low':
      return <Badge variant="secondary">Low</Badge>;
    default:
      return <Badge variant="outline">{priority || 'None'}</Badge>;
  }
};

// Format renewal period for display
const formatRenewalPeriod = (period) => {
  if (!period) return null;
  const periodMap = {
    'none': 'No Renewal',
    '6_months': '6 Months',
    '1_year': '1 Year',
    '2_years': '2 Years',
    '3_years': '3 Years',
    '5_years': '5 Years',
    'custom': 'Custom'
  };
  return periodMap[period] || period;
};

const QualificationsPage = ({ stackId, stackName, stackImage, stackProjects, isStackView = false }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { canEditTraining } = useAuth();

  // Set page title
  usePageTitle(isStackView ? `${stackName} Qualifications` : 'Qualifications');

  // Check if user has permission to edit training/qualifications
  const hasEditPermission = canEditTraining();

  // Memoize projectIds to prevent infinite re-renders
  const projectIds = React.useMemo(() => {
    return isStackView ? stackProjects?.map(p => p.id) : [projectId];
  }, [isStackView, stackProjects, projectId]);

  // Space filter for stack view
  const [selectedSpaceFilter, setSelectedSpaceFilter] = useState('all');

  // State
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingRequirement, setEditingRequirement] = useState(null);
  const [space, setSpace] = useState(null);
  const [spaceTools, setSpaceTools] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => getStoredPageSize(25));

  // Inline editing state
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [editingType, setEditingType] = useState('number');

  // Gap analysis state
  const [openGapAnalysisId, setOpenGapAnalysisId] = useState(null);

  // Column sorting
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimerRef = useRef(null);

  // SINGLE SOURCE OF TRUTH: Compliance stats from RPC
  // This matches the matrix tool's compliance calculations
  const {
    data: complianceStatsData,
    loading: complianceStatsLoading,
    refetch: refetchComplianceStats
  } = useComplianceStats(projectId, {
    enabled: !!projectId && !isStackView // Only fetch for single projects
  });

  // Combined loading state: show loading when either data source is loading
  // For single-space view, we need both qualifications data AND compliance stats
  const isDataLoading = loading || (!isStackView && complianceStatsLoading);

  // Debounce search input (300ms)
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1); // Reset to first page on search
    }, 300);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  // Reusable fetch function using optimized RPC
  const fetchQualificationsData = useCallback(async (showLoadingSpinner = true) => {
    try {
      if (showLoadingSpinner) {
        setLoading(true);
      }

      const idsToFetch = isStackView && projectIds?.length > 0
        ? projectIds
        : projectId ? [projectId] : [];

      if (idsToFetch.length === 0) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.rpc('get_space_qualifications_data', {
        p_project_ids: idsToFetch
      });

      if (error) throw error;

      // Filter out documents and checklists - only show actual qualifications/courses
      const filteredRequirements = (data?.requirements || []).filter(
        r => r.requirement_type !== 'documentation' && r.requirement_type !== 'internal_checklist'
      );

      setRequirements(filteredRequirements);

      if (!isStackView && data?.space) {
        setSpace(data.space);

        if (data.space.tools) {
          const toolsObj = typeof data.space.tools === 'string'
            ? JSON.parse(data.space.tools)
            : data.space.tools;
          const tools = Object.entries(toolsObj)
            .filter(([, enabled]) => enabled === true)
            .map(([toolKey]) => ({
              id: toolKey,
              tool_type: toolKey,
              name: toolConfigs[toolKey]?.name || toolKey,
              icon: toolConfigs[toolKey]?.icon,
              color: toolConfigs[toolKey]?.color
            }));
          setSpaceTools(tools);
        }
      }
    } catch (error) {
      console.error('Error loading qualifications:', error);
      toast.error('Failed to load qualifications');
      setRequirements([]);
    } finally {
      if (showLoadingSpinner) {
        setLoading(false);
      }
    }
  }, [projectId, isStackView, projectIds]);

  // Load data on mount
  useEffect(() => {
    if (projectId || (isStackView && projectIds && projectIds.length > 0)) {
      if (isStackView) {
        const standardTools = [
          { id: 'matrix', tool_type: 'matrix', name: toolConfigs.matrix.name, icon: toolConfigs.matrix.icon, color: toolConfigs.matrix.color },
          { id: 'team', tool_type: 'team', name: toolConfigs.team.name, icon: toolConfigs.team.icon, color: toolConfigs.team.color },
          { id: 'qualifications', tool_type: 'qualifications', name: toolConfigs.qualifications.name, icon: toolConfigs.qualifications.icon, color: toolConfigs.qualifications.color },
          { id: 'bookings', tool_type: 'bookings', name: toolConfigs.bookings.name, icon: toolConfigs.bookings.icon, color: toolConfigs.bookings.color },
          { id: 'calendar', tool_type: 'calendar', name: toolConfigs.calendar.name, icon: toolConfigs.calendar.icon, color: toolConfigs.calendar.color },
          { id: 'checklists', tool_type: 'checklists', name: toolConfigs.checklists.name, icon: toolConfigs.checklists.icon, color: toolConfigs.checklists.color },
          { id: 'docs', tool_type: 'docs', name: toolConfigs.docs.name, icon: toolConfigs.docs.icon, color: toolConfigs.docs.color },
          { id: 'message_board', tool_type: 'message_board', name: toolConfigs.message_board.name, icon: toolConfigs.message_board.icon, color: toolConfigs.message_board.color },
          { id: 'checkins', tool_type: 'checkins', name: toolConfigs.checkins.name, icon: toolConfigs.checkins.icon, color: toolConfigs.checkins.color },
        ];
        setSpaceTools(standardTools);
      }
      fetchQualificationsData();
    }
  }, [fetchQualificationsData, isStackView, projectId, projectIds]);

  // SINGLE SOURCE OF TRUTH: Build stats map from compliance stats RPC
  // For single-space view, use calculate_compliance_stats (matches matrix tool)
  // For stack view, use embedded stats from get_space_qualifications_data
  const qualStatsMap = useMemo(() => {
    const statsMap = new Map();

    // For single-space view: Use compliance stats from dedicated RPC (single source of truth)
    if (!isStackView && complianceStatsData?.by_requirement) {
      complianceStatsData.by_requirement.forEach(stat => {
        statsMap.set(stat.requirement_id, {
          totalAssigned: stat.assigned_count || 0,
          valid: stat.valid || 0,
          validCrossSpace: stat.valid_cross_space || 0,
          expiring: stat.expiring || 0,
          expiringCrossSpace: stat.expiring_cross_space || 0,
          expired: stat.expired || 0,
          missing: stat.missing || 0,
          booked: stat.booked || 0,
          quoting: stat.quoting || 0,
          required: stat.required || 0,
          compliancePercentage: stat.compliance_percentage || 0
        });
      });
      return statsMap;
    }

    // For stack view: Use embedded stats from get_space_qualifications_data
    requirements.forEach(qual => {
      if (qual.stats) {
        statsMap.set(qual.id, {
          totalAssigned: qual.stats.totalAssigned || 0,
          valid: qual.stats.valid || 0,
          validCrossSpace: qual.stats.validCrossSpace || 0,
          expiring: qual.stats.expiring || 0,
          expiringCrossSpace: qual.stats.expiringCrossSpace || 0,
          expired: qual.stats.expired || 0,
          missing: qual.stats.missing || 0,
          booked: qual.stats.booked || 0,
          quoting: qual.stats.quoting || 0,
          required: qual.stats.required || 0,
          compliancePercentage: qual.stats.compliancePercentage || 0
        });
      } else {
        statsMap.set(qual.id, {
          totalAssigned: 0, valid: 0, validCrossSpace: 0, expiring: 0,
          expiringCrossSpace: 0, expired: 0, missing: 0, booked: 0,
          quoting: 0, required: 0, compliancePercentage: 0
        });
      }
    });
    return statsMap;
  }, [requirements, isStackView, complianceStatsData]);

  // Combined refetch function for both qualifications data and compliance stats
  const refetchAllData = useCallback(async (showLoading = false) => {
    await fetchQualificationsData(showLoading);
    if (!isStackView) {
      refetchComplianceStats();
    }
  }, [fetchQualificationsData, isStackView, refetchComplianceStats]);

  const handleEdit = useCallback((requirement) => {
    setEditingRequirement(requirement);
    setIsEditModalOpen(true);
  }, []);

  const handleDelete = useCallback(async (requirement) => {
    if (!confirm(`Are you sure you want to delete "${requirement.name}"? This will also remove all assignments.`)) {
      return;
    }
    try {
      const { error } = await supabase
        .from('compliance_requirements')
        .delete()
        .eq('id', requirement.id);
      if (error) throw error;
      toast.success('Qualification deleted successfully');
      refetchAllData(false);
    } catch (error) {
      console.error('Error deleting requirement:', error);
      toast.error('Failed to delete qualification');
    }
  }, [refetchAllData]);

  // Get projects list for AddQualification modal
  const projectsList = React.useMemo(() => {
    if (isStackView && stackProjects) {
      return stackProjects.map(p => ({ id: p.id, name: p.name }));
    } else if (space) {
      return [{ id: space.id, name: space.name }];
    }
    return [];
  }, [isStackView, stackProjects, space]);

  // Filter by selected space (for stack view)
  const spaceFilteredRequirements = React.useMemo(() => {
    if (!isStackView || selectedSpaceFilter === 'all') {
      return requirements;
    }
    return requirements.filter(r => r.project_id === selectedSpaceFilter);
  }, [requirements, isStackView, selectedSpaceFilter]);

  // Filter requirements by search
  const searchFilteredRequirements = useMemo(() => {
    if (!debouncedSearch.trim()) return spaceFilteredRequirements;
    const searchLower = debouncedSearch.toLowerCase();
    return spaceFilteredRequirements.filter(req =>
      req.name.toLowerCase().includes(searchLower) ||
      req.description?.toLowerCase().includes(searchLower)
    );
  }, [spaceFilteredRequirements, debouncedSearch]);

  // Sort requirements
  const sortedRequirements = useMemo(() => {
    if (!sortColumn) return searchFilteredRequirements;

    return [...searchFilteredRequirements].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'priority': {
          const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
          aValue = priorityOrder[a.priority] || 0;
          bValue = priorityOrder[b.priority] || 0;
          break;
        }
        case 'required': {
          const aStats = qualStatsMap.get(a.id);
          const bStats = qualStatsMap.get(b.id);
          aValue = aStats?.required || 0;
          bValue = bStats?.required || 0;
          break;
        }
        case 'compliance': {
          const aCompStats = qualStatsMap.get(a.id);
          const bCompStats = qualStatsMap.get(b.id);
          aValue = aCompStats?.compliancePercentage || 0;
          bValue = bCompStats?.compliancePercentage || 0;
          break;
        }
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [searchFilteredRequirements, sortColumn, sortDirection, qualStatsMap]);

  // Pagination calculations
  const totalPages = Math.ceil(sortedRequirements.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRequirements = sortedRequirements.slice(startIndex, endIndex);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedSpaceFilter, sortColumn, sortDirection]);

  // Handle column header click for sorting
  const handleSort = useCallback((column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn]);

  // Cancel inline editing
  const cancelEditing = useCallback(() => {
    setEditingCell(null);
    setEditingValue('');
    setEditingType('number');
  }, []);

  // Inline editing handlers
  const startEditing = useCallback((requirementId, field, currentValue) => {
    setEditingCell({ requirementId, field });

    if (field === 'calculated_required') {
      const requirement = requirements.find(r => r.id === requirementId);
      const minimumPersonnel = requirement?.minimum_personnel;

      if (minimumPersonnel && typeof minimumPersonnel === 'string' && minimumPersonnel.includes('%')) {
        setEditingType('percentage');
      } else {
        setEditingType('number');
      }
      setEditingValue(currentValue || '');
    } else {
      setEditingValue(currentValue || '');
    }
  }, [requirements]);

  const saveInlineEdit = useCallback(async (requirementId, field, directValue = null) => {
    try {
      const valueToSave = directValue !== null ? directValue : editingValue;

      let updateData = {
        updated_date: new Date().toISOString()
      };

      if (field === 'calculated_required') {
        if (valueToSave === '' || valueToSave === null) {
          updateData.calculated_required = null;
          updateData.minimum_personnel = null;
        } else {
          updateData.calculated_required = parseInt(valueToSave);
          updateData.minimum_personnel = editingType === 'percentage'
            ? `${valueToSave}%`
            : String(valueToSave);
        }
      } else {
        updateData[field] = valueToSave;
      }

      const { error } = await supabase
        .from('compliance_requirements')
        .update(updateData)
        .eq('id', requirementId);

      if (error) throw error;

      await refetchAllData(false);
      toast.success('Updated successfully');
      cancelEditing();
    } catch (error) {
      console.error('Error updating:', error);
      toast.error('Failed to update');
    }
  }, [editingValue, editingType, refetchAllData, cancelEditing]);

  const handleKeyDown = useCallback((e, requirementId, field) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveInlineEdit(requirementId, field);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  }, [saveInlineEdit, cancelEditing]);

  // Calculate totals for footer row
  const totals = useMemo(() => {
    const result = sortedRequirements.reduce((acc, qual) => {
      const stats = qualStatsMap.get(qual.id) || {
        totalAssigned: 0, valid: 0, expiring: 0, expired: 0,
        missing: 0, booked: 0, quoting: 0, required: 0, compliancePercentage: 0
      };

      return {
        required: acc.required + stats.required,
        assigned: acc.assigned + stats.totalAssigned,
        valid: acc.valid + stats.valid + (stats.validCrossSpace || 0),
        expiring: acc.expiring + stats.expiring + (stats.expiringCrossSpace || 0),
        expired: acc.expired + stats.expired,
        missing: acc.missing + stats.missing,
        booked: acc.booked + stats.booked,
        quoting: acc.quoting + stats.quoting,
      };
    }, { required: 0, assigned: 0, valid: 0, expiring: 0, expired: 0, missing: 0, booked: 0, quoting: 0 });

    // Calculate overall compliance using AVERAGE method
    let totalCompliancePercentage = 0;
    let qualificationCount = 0;

    sortedRequirements.forEach(qual => {
      const stats = qualStatsMap.get(qual.id);
      if (stats) {
        const totalValid = stats.valid + (stats.validCrossSpace || 0);
        const totalExpiring = stats.expiring + (stats.expiringCrossSpace || 0);
        const compliantCount = totalValid + totalExpiring;
        const individualCompliance = stats.required > 0
          ? Math.min(100, Math.round((compliantCount / stats.required) * 100))
          : 0;
        totalCompliancePercentage += individualCompliance;
        qualificationCount++;
      }
    });

    result.overallCompliance = qualificationCount > 0
      ? Math.round(totalCompliancePercentage / qualificationCount)
      : 0;
    result.compliantCount = result.valid + result.expiring;

    return result;
  }, [sortedRequirements, qualStatsMap]);

  // Render sort indicator
  const renderSortIndicator = (column) => {
    if (sortColumn === column) {
      return sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
    }
    return <ArrowUpDown className="h-3 w-3 opacity-50" />;
  };

  return (
    <PageTransition>
      <AppShell3 project={space}>
        <ToolHeader
          spaceName={isStackView ? stackName : (space?.name || 'Space')}
          spaceImage={isStackView ? stackImage : space?.logo_url}
          toolName="Qualifications"
          currentTool="qualifications"
          spaceTools={spaceTools}
          stackId={stackId}
          isStackView={isStackView}
          selectedSpaceFilter={selectedSpaceFilter}
          onSpaceFilterChange={setSelectedSpaceFilter}
          stackProjects={stackProjects}
          actions={
            <>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search qualifications..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-8 bg-white text-sm"
                />
              </div>
              {hasEditPermission && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAddModalOpen(true)}
                  className="gap-2 h-8"
                >
                  <Plus className="w-4 h-4" />
                  Add Qualification
                </Button>
              )}
            </>
          }
        />

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <TableCard.Root>
              <TableCard.Header
                title="Qualifications"
                badge={`${sortedRequirements.length} qualifications`}
              />
              <Table aria-label="Qualifications">
                <Table.Header>
                  <Table.Head id="name" label="Name" isRowHeader className="w-[28%]" />
                  <Table.Head id="priority" label="Priority" className="w-[10%]" />
                  <Table.Head id="required" label="Required" className="w-[10%]" />
                  <Table.Head id="validity" label="Validity" className="w-[12%]" />
                  <Table.Head id="compliance" label="Compliance" className="w-[20%]" />
                  <Table.Head id="quote" label="" className="w-[12%]" />
                  <Table.Head id="actions" label="" className="w-[8%]" />
                </Table.Header>

                <Table.Body items={isDataLoading ? skeletonItems : paginatedRequirements}>
                  {(requirement) => (
                    <Table.Row id={requirement.id}>
                      {/* Name Cell */}
                      <Table.Cell>
                        {requirement.isLoading ? (
                          <div className="space-y-1">
                            <Skeleton className="h-4 w-40" />
                            <Skeleton className="h-3 w-24" />
                          </div>
                        ) : editingCell?.requirementId === requirement.id && editingCell?.field === 'name' ? (
                          <div className="flex-1">
                            <Input
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, requirement.id, 'name')}
                              onBlur={() => saveInlineEdit(requirement.id, 'name')}
                              className="h-8 text-sm border-blue-300"
                              autoFocus
                            />
                          </div>
                        ) : (
                          <div
                            className="cursor-pointer hover:bg-gray-50 rounded px-2 py-1.5 -mx-2 -my-1.5 transition-colors group"
                            onClick={() => startEditing(requirement.id, 'name', requirement.name)}
                          >
                            <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                              {requirement.name}
                            </div>
                            {requirement.description && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="text-xs text-gray-500 truncate max-w-[250px]">
                                      {requirement.description}
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-sm">
                                    <p>{requirement.description}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        )}
                      </Table.Cell>

                      {/* Priority Cell */}
                      <Table.Cell>
                        {requirement.isLoading ? (
                          <Skeleton className="h-5 w-16 rounded" />
                        ) : editingCell?.requirementId === requirement.id && editingCell?.field === 'priority' ? (
                          <Select
                            value={editingValue}
                            onValueChange={(value) => saveInlineEdit(requirement.id, 'priority', value)}
                            defaultOpen
                          >
                            <SelectTrigger className="h-7 w-full text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="low">Low</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <div
                            className="cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5 -mx-1 -my-0.5 transition-colors inline-block"
                            onClick={() => startEditing(requirement.id, 'priority', requirement.priority)}
                          >
                            {getPriorityBadge(requirement.priority)}
                          </div>
                        )}
                      </Table.Cell>

                      {/* Required Cell */}
                      <Table.Cell>
                        {requirement.isLoading ? (
                          <Skeleton className="h-4 w-12" />
                        ) : editingCell?.requirementId === requirement.id && editingCell?.field === 'calculated_required' ? (
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, requirement.id, 'calculated_required')}
                              onBlur={() => saveInlineEdit(requirement.id, 'calculated_required')}
                              className="h-6 w-14 text-xs px-1.5"
                              placeholder="0"
                              min="0"
                              max={editingType === 'percentage' ? 100 : undefined}
                              autoFocus
                            />
                            <button
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setEditingType(editingType === 'number' ? 'percentage' : 'number');
                              }}
                              className="h-6 w-6 flex items-center justify-center rounded hover:bg-gray-100 text-xs font-semibold text-gray-600"
                              title={editingType === 'number' ? 'Switch to percentage' : 'Switch to number'}
                            >
                              {editingType === 'percentage' ? '%' : '#'}
                            </button>
                          </div>
                        ) : (
                          <div
                            className="flex items-center gap-1.5 cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5 -mx-1 -my-0.5 transition-colors group"
                            onClick={() => startEditing(requirement.id, 'calculated_required', requirement.calculated_required)}
                          >
                            <Users className="h-3 w-3 text-gray-400" />
                            <span className="text-sm text-gray-700">
                              {qualStatsMap.get(requirement.id)?.required || 'All'}
                            </span>
                          </div>
                        )}
                      </Table.Cell>

                      {/* Validity Cell */}
                      <Table.Cell>
                        {requirement.isLoading ? (
                          <Skeleton className="h-4 w-16" />
                        ) : editingCell?.requirementId === requirement.id && editingCell?.field === 'renewal_period' ? (
                          <Select
                            value={editingValue}
                            onValueChange={(value) => saveInlineEdit(requirement.id, 'renewal_period', value)}
                            defaultOpen
                          >
                            <SelectTrigger className="h-7 w-full text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No Renewal</SelectItem>
                              <SelectItem value="6_months">6 Months</SelectItem>
                              <SelectItem value="1_year">1 Year</SelectItem>
                              <SelectItem value="2_years">2 Years</SelectItem>
                              <SelectItem value="3_years">3 Years</SelectItem>
                              <SelectItem value="5_years">5 Years</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <div
                            className="cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5 -mx-1 -my-0.5 transition-colors inline-flex items-center gap-1 group"
                            onClick={() => startEditing(requirement.id, 'renewal_period', requirement.renewal_period)}
                          >
                            {requirement.renewal_period ? (
                              <>
                                <Clock className="h-3 w-3 text-gray-400" />
                                <span className="text-xs text-gray-700">{formatRenewalPeriod(requirement.renewal_period)}</span>
                              </>
                            ) : (
                              <span className="text-xs text-gray-400">Set validity</span>
                            )}
                          </div>
                        )}
                      </Table.Cell>

                      {/* Compliance Cell */}
                      <Table.Cell>
                        {requirement.isLoading ? (
                          <div className="space-y-1">
                            <Skeleton className="h-3 w-full" />
                            <Skeleton className="h-2 w-full rounded-full" />
                          </div>
                        ) : (() => {
                          const stats = qualStatsMap.get(requirement.id) || { compliancePercentage: 0, valid: 0, validCrossSpace: 0, expiring: 0, expiringCrossSpace: 0, required: 0, expired: 0, missing: 0, booked: 0, quoting: 0 };
                          return (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="w-full min-w-[100px] cursor-pointer">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-xs font-medium text-gray-600">
                                        {stats.compliancePercentage}%
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        {stats.valid + stats.validCrossSpace + stats.expiring + stats.expiringCrossSpace}/{stats.required}
                                      </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                      <div
                                        className={`h-full rounded-full transition-all duration-300 ${
                                          stats.compliancePercentage >= 80 ? 'bg-green-500' :
                                          stats.compliancePercentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                                        }`}
                                        style={{ width: `${stats.compliancePercentage}%` }}
                                      />
                                    </div>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <div className="space-y-1 text-xs">
                                    <div className="font-semibold border-b pb-1 mb-1">Compliance Breakdown</div>
                                    <div className="flex justify-between gap-4">
                                      <span className="text-green-400">Valid:</span>
                                      <span className="font-medium">{stats.valid + stats.validCrossSpace}</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                      <span className="text-yellow-400">Expiring:</span>
                                      <span className="font-medium">{stats.expiring + stats.expiringCrossSpace}</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                      <span className="text-red-400">Expired:</span>
                                      <span className="font-medium">{stats.expired}</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                      <span className="text-orange-400">Missing:</span>
                                      <span className="font-medium">{stats.missing}</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                      <span className="text-blue-400">Booked:</span>
                                      <span className="font-medium">{stats.booked}</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                      <span className="text-purple-400">Quoting:</span>
                                      <span className="font-medium">{stats.quoting}</span>
                                    </div>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          );
                        })()}
                      </Table.Cell>

                      {/* Quote Button Cell */}
                      <Table.Cell>
                        {requirement.isLoading ? (
                          <Skeleton className="h-8 w-20 rounded" />
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8"
                            onClick={() => {
                              navigate(`/space/${projectId}/build-quote?course=${encodeURIComponent(requirement.name)}&from=${encodeURIComponent(window.location.pathname)}`);
                            }}
                          >
                            <FileText className="h-3.5 w-3.5 mr-1.5" />
                            Quote
                          </Button>
                        )}
                      </Table.Cell>

                      {/* Actions Cell */}
                      <Table.Cell>
                        {requirement.isLoading ? (
                          <div className="flex justify-end">
                            <Skeleton className="h-8 w-8 rounded" />
                          </div>
                        ) : (
                          <div className="flex justify-end">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => {
                                  navigate(`/space/${projectId}/matrix`, {
                                    state: { viewMode: 'qualifications', qualificationId: requirement.id }
                                  });
                                }}>
                                  <Eye className="h-4 w-4 mr-2" />
                                  View on Matrix
                                </DropdownMenuItem>
                                {hasEditPermission && (
                                  <>
                                    <DropdownMenuItem onClick={() => handleEdit(requirement)}>
                                      <Edit2 className="h-4 w-4 mr-2" />
                                      Edit All
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleDelete(requirement)}
                                      className="text-red-600"
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>

              {/* Empty State */}
              {!isDataLoading && sortedRequirements.length === 0 && (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No qualifications found
                  </h3>
                  <p className="text-gray-500 mb-4">
                    {searchQuery
                      ? 'Try adjusting your search'
                      : hasEditPermission
                        ? 'Click "Add Qualification" to create your first qualification'
                        : 'No qualifications have been added yet'}
                  </p>
                  {!searchQuery && hasEditPermission && (
                    <Button onClick={() => setIsAddModalOpen(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add First Qualification
                    </Button>
                  )}
                </div>
              )}

              {/* Totals Row */}
              {!isDataLoading && sortedRequirements.length > 0 && (
                <div className="px-6 py-3 bg-gray-50 border-t-2 border-gray-300">
                  <div className="flex items-center">
                    <div className="w-[30%] font-semibold text-gray-900">TOTALS</div>
                    <div className="w-[12%] text-gray-500">-</div>
                    <div className="w-[12%] text-sm font-medium text-gray-700">{totals.required}</div>
                    <div className="w-[14%] text-gray-500">-</div>
                    <div className="w-[22%]">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="w-full min-w-[100px] cursor-pointer">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-semibold text-gray-700">
                                  {totals.overallCompliance}%
                                </span>
                                <span className="text-xs text-gray-600">
                                  {totals.compliantCount}/{totals.required}
                                </span>
                              </div>
                              <div className="w-full bg-gray-300 rounded-full h-2 overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all duration-300 ${
                                    totals.overallCompliance >= 80 ? 'bg-green-600' :
                                    totals.overallCompliance >= 50 ? 'bg-yellow-600' : 'bg-red-600'
                                  }`}
                                  style={{ width: `${totals.overallCompliance}%` }}
                                />
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="space-y-1 text-xs">
                              <div className="font-semibold border-b pb-1 mb-1">Overall Totals</div>
                              <div className="flex justify-between gap-4">
                                <span className="text-green-400">Valid:</span>
                                <span className="font-medium">{totals.valid}</span>
                              </div>
                              <div className="flex justify-between gap-4">
                                <span className="text-yellow-400">Expiring:</span>
                                <span className="font-medium">{totals.expiring}</span>
                              </div>
                              <div className="flex justify-between gap-4">
                                <span className="text-red-400">Expired:</span>
                                <span className="font-medium">{totals.expired}</span>
                              </div>
                              <div className="flex justify-between gap-4">
                                <span className="text-orange-400">Missing:</span>
                                <span className="font-medium">{totals.missing}</span>
                              </div>
                              <div className="flex justify-between gap-4">
                                <span className="text-blue-400">Booked:</span>
                                <span className="font-medium">{totals.booked}</span>
                              </div>
                              <div className="flex justify-between gap-4">
                                <span className="text-purple-400">Quoting:</span>
                                <span className="font-medium">{totals.quoting}</span>
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="w-[10%] text-gray-500">-</div>
                  </div>
                </div>
              )}

              {/* Pagination */}
              {sortedRequirements.length > 0 && (
                <div className="px-4 py-3 md:px-6 md:pt-3 md:pb-4 border-t">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">
                      Showing <span className="font-medium">{sortedRequirements.length > 0 ? startIndex + 1 : 0}</span> to{' '}
                      <span className="font-medium">{Math.min(endIndex, sortedRequirements.length)}</span> of{' '}
                      <span className="font-medium">{sortedRequirements.length}</span> results
                    </span>

                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-700">Per page:</span>
                        <select
                          value={itemsPerPage}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            setItemsPerPage(val);
                            setCurrentPage(1);
                            setStoredPageSize(val);
                          }}
                          className="border border-gray-300 rounded px-2 py-1 text-sm"
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>

                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                        >
                          First
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </Button>

                        <div className="flex items-center gap-1">
                          {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(page => {
                              return (
                                page === 1 ||
                                page === totalPages ||
                                (page >= currentPage - 1 && page <= currentPage + 1)
                              );
                            })
                            .map((page, index, array) => {
                              const prevPage = array[index - 1];
                              const showEllipsis = prevPage && page - prevPage > 1;

                              return (
                                <span key={page} className="contents">
                                  {showEllipsis && (
                                    <span className="px-2 text-gray-500">...</span>
                                  )}
                                  <Button
                                    variant={currentPage === page ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setCurrentPage(page)}
                                    className="w-9"
                                  >
                                    {page}
                                  </Button>
                                </span>
                              );
                            })}
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages || totalPages === 0}
                        >
                          Next
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage === totalPages || totalPages === 0}
                        >
                          Last
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </TableCard.Root>
          </div>
        </div>

        {/* Add Qualification Modal */}
        <AddQualification
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          projects={projectsList}
          onSuccess={() => refetchAllData(false)}
          defaultProjectId={projectId}
        />

        {/* Edit Qualification Modal */}
        <EditQualification
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingRequirement(null);
          }}
          selectedQualification={editingRequirement}
          onSuccess={() => refetchAllData(false)}
        />

        {/* Gap Analysis Dialogs */}
        {sortedRequirements.map((qual) => {
          const stats = qualStatsMap.get(qual.id) || {
            totalAssigned: 0, valid: 0, expiring: 0, expired: 0,
            missing: 0, booked: 0, quoting: 0, required: 0, compliancePercentage: 0
          };

          return (
            <Dialog
              key={qual.id}
              open={openGapAnalysisId === qual.id}
              onOpenChange={(open) => { if (!open) setOpenGapAnalysisId(null); }}
            >
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{qual.name} - GAP Analysis</DialogTitle>
                </DialogHeader>

                <div className="space-y-3 py-4">
                  {qual.description && (
                    <p className="text-sm text-gray-500">{qual.description}</p>
                  )}

                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Overall Compliance</span>
                      <span className="text-2xl font-bold text-gray-900">{stats.compliancePercentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${
                          stats.compliancePercentage >= 80 ? 'bg-green-500' :
                          stats.compliancePercentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${stats.compliancePercentage}%` }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-2.5">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-green-500" />
                        <span className="text-xs font-medium text-green-900">Valid</span>
                      </div>
                      <div className="text-2xl font-bold text-green-700 mt-1">{stats.valid}</div>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2.5">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-yellow-500" />
                        <span className="text-xs font-medium text-yellow-900">Expiring</span>
                      </div>
                      <div className="text-2xl font-bold text-yellow-700 mt-1">{stats.expiring}</div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-2.5">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-red-500" />
                        <span className="text-xs font-medium text-red-900">Expired</span>
                      </div>
                      <div className="text-2xl font-bold text-red-700 mt-1">{stats.expired}</div>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-2.5">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-orange-500" />
                        <span className="text-xs font-medium text-orange-900">Missing</span>
                      </div>
                      <div className="text-2xl font-bold text-orange-700 mt-1">{stats.missing}</div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-blue-500" />
                        <span className="text-xs font-medium text-blue-900">Booked</span>
                      </div>
                      <div className="text-2xl font-bold text-blue-700 mt-1">{stats.booked}</div>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-2.5">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-purple-500" />
                        <span className="text-xs font-medium text-purple-900">Quoting</span>
                      </div>
                      <div className="text-2xl font-bold text-purple-700 mt-1">{stats.quoting}</div>
                    </div>
                  </div>

                  <div className="border-t pt-3 space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Required:</span>
                      <span className="font-semibold text-gray-900">{stats.required || 'All'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total Assigned:</span>
                      <span className="font-semibold text-gray-900">{stats.totalAssigned}</span>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          );
        })}
      </AppShell3>
    </PageTransition>
  );
};

export default QualificationsPage;
